import { PageHead } from "@/components/utils";
import { RevealText, FadeUp, ClipImage } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { IMAGES, CHAPTERS } from "@/lib/data";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

export default function About() {
  return (
    <>
      <PageHead
        title="About — Good Looks Unisex Salon, Mansa"
        description="Meet Good Looks: a modern unisex salon in Sunni Gali, Sadar, Mansa, built around consultation and craft."
      />
      <section className="bg-[#F3EAD9] text-[#1B1B1B] pt-40 pb-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <FadeUp>
            <p className="eyebrow text-[#C89B3C] mb-6">— About the salon</p>
            <h1 className="font-display text-5xl md:text-8xl leading-[0.95] max-w-5xl">
              A neighbourhood<br />atelier for<br /><span className="italic">hair, skin & bridal.</span>
            </h1>
          </FadeUp>

          <div className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <FadeUp className="lg:col-span-5" delay={0.1}>
              <ClipImage src={IMAGES.aboutInterior} alt="Studio interior" className="aspect-[3/4]" />
            </FadeUp>
            <div className="lg:col-span-7 space-y-8 lg:pt-16">
              <FadeUp>
                <p className="text-black/70 text-lg leading-relaxed max-w-xl">
                  Good Looks was created as a quiet counterpoint to the loud,
                  fast beauty industry. A place where you can sit down, be
                  listened to, and leave feeling like the best version of
                  yourself — nothing more performative than that.
                </p>
              </FadeUp>
              <FadeUp delay={0.05}>
                <p className="text-black/70 text-lg leading-relaxed max-w-xl">
                  Our unisex studio in Sunni Gali, Sadar welcomes walk-ins and
                  appointments alike, and our bridal chapter is planned across
                  sittings so nothing feels rushed on the day.
                </p>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      <Marquee items={["Hair", "Skin", "Bridal", "Colour", "Studio"]} className="bg-[#1B1B1B] text-[#C89B3C]" />

      <section className="bg-[#F3EAD9] py-24 md:py-40">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <FadeUp>
              <p className="eyebrow text-[#C89B3C] mb-6">— How we work</p>
              <h2 className="font-display text-4xl md:text-5xl leading-[1.02]">
                Three quiet<br /><span className="italic">principles.</span>
              </h2>
            </FadeUp>
          </div>
          <div className="lg:col-span-8 space-y-14">
            {CHAPTERS.map((c, i) => (
              <FadeUp key={c.n} delay={i * 0.05}>
                <div className="grid grid-cols-12 gap-6 md:gap-10 border-t border-black/10 pt-10">
                  <div className="col-span-12 md:col-span-4">
                    <div className="aspect-[4/5] overflow-hidden bg-black/5">
                      <img
                        src={c.img}
                        alt={c.title}
                        loading="lazy"
                        className="w-full h-full object-cover hover:scale-[1.05] transition-transform duration-[1400ms]"
                      />
                    </div>
                  </div>
                  <div className="col-span-12 md:col-span-8">
                    <div className="chapter-number text-3xl md:text-4xl mb-3">{c.n}</div>
                    <h3 className="font-display text-2xl md:text-3xl mb-3">{c.title}</h3>
                    <p className="text-black/65 leading-relaxed max-w-xl">{c.body}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#1B1B1B] text-[#F3EAD9] py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
          <FadeUp>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.98] max-w-2xl">
              Ready to book<br /><span className="italic text-[#C89B3C]">your first visit?</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <Link to="/book" className="btn-gold">
              <span>Book Appointment</span>
              <ArrowUpRight size={16} />
            </Link>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
